import React, { memo, useEffect, useState, useCallback, useRef } from "react";
import styled, { useTheme } from "styled-components";
import { usePrevious, useToggle } from "react-use";
import { bool, func } from "prop-types";
import getConfig from "next/config";
import { useTranslation } from "@lib/i18n";

import Subheading from "../shared/Subheading";
import ErrorBoundary from "../shared/ErrorBoundary";
import Switch from "../shared/Switch";
import Typography from "../shared/Typography";
import gtm from "@gtm/core";

import {
  color,
  tabletLarge,
  desktop,
  fontSize,
  fontWeight,
  font,
} from "../shared/utils";

import {
  useActiveCarPreconfigurationSelector,
  useActiveAccessorySelector,
} from "@redux/reducers/deal";

import { usePrimaryColor } from "@hooks/usePrimaryColor";

import { changeModelViewEvent } from "@gtm/events/homepage";
import { useCarSelector } from "@redux/reducers/appConfig";
import { getId360ViewConfig } from "@lib/getId360ViewConfig";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";

const { publicRuntimeConfig } = getConfig();
const { BUSINESS_ENV, CDN_BASE_URL, COMMIT_HASH } = publicRuntimeConfig;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${color("white")};
  border-radius: 0.7rem;
  width: 88vw;
  height: 88vw;
  padding: 2rem 0 1rem;
  overflow: hidden;
  max-width: 40rem;
  max-height: 35rem;

  ${tabletLarge`
    height: 100%;
    width: 100%;
  `}

  ${desktop`
    border-radius: 0.4rem;
  `}
`;

const IFrame = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  border: 0;
  background: ${color("white")};
`;

const StyledHeading = styled(Subheading)`
  z-index: 1;
  white-space: nowrap;
  text-transform: uppercase;
  font-size: ${fontSize("xl")};
  font-weight: ${fontWeight("bolder")};
  font-family: ${font("citroen")};

  ${desktop`
    font-size: ${fontSize("xxl")};
  `}
`;

const SwitcherWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 0;
  z-index: 1;
`;

const StyledTypography = styled(Typography)`
  transition: color 0.2s ease-in;

  &:first-of-type {
    margin-right: 1.4rem;
  }

  &:last-of-type {
    margin-left: 1.4rem;
  }
`;

// These models are available in the current 360 version - attempting to load other ids would lead to an error.
const ALLOWED_ACCESSORY_360_VIEW_IDS = [
  "1668221880",
  "1668221980",
  "1668222080",
  "1668222180",
  "1668279480",
  "1668279880",
  "1668279980",
  "1668280080",
  "1668280180",
  "1668281080",
  "1668281180",
  "1668281280",
  "1668281380",
  "1673383780",
  "1674579280",
  "1674579380",
  "1674579480",
  "1674579580",
  "1674579680",
  "1674579780",
  "1668281499",
  "1668281480",
  "1674579880",
  "1668280181",
  "AC01a",
  "AC01b",
  "AC01c",
  "AC01d",
  "AC05a",
  "AC05b",
  "AC05c",
  "AC05d",
  "AC06a",
  "AC06b",
  "AC06c",
  "AC06d",
  "AC10",
  "AC12",
  "AC14",
  "AC15",
  "AC16",
  "AC17",
  "AC18",
  "AC19",
  "AC22",
  "AC24",
  "AC25",
  "AC27",
  "AC29",
  "AC30",
  "AC31",
  "AC32",
  "AC33",
  "AC34",
  "AC35",
  "AC36",
  "AC41",
  "AC49",
];
// [
//   "1668279480",
//   "1673383780",
//   "AC10",
//   "AC19",
//   "AC14",
//   "AC18",
//   "AC12",
//   "AC01c",
//   "1668222080",
//   "AC05c",
//   "AC06c",
//   "1668279880",
//   "AC25",
//   "AC27",
//   "AC41",
//   "AC33",
//   "AC34",
//   "AC31",
//   "1668281499",
//   "1668281480",
//   "1674579880",
//   "1668280181",
// ];

const ACCESSORIES_WITHOUT_360_MODEL = ["AC21"];

const ThreeSixty = ({
  threeSixtyOpen,
  onLoad,
  setCurrentLoadingProgress,
  shouldDisplayProgressBar,
  setShouldDisplayProgressBar,
}) => {
  const { t } = useTranslation();
  const primaryColor = usePrimaryColor();
  const activePreConfiguration = useActiveCarPreconfigurationSelector();
  const activeAccessory = useActiveAccessorySelector();
  const car = useCarSelector();
  const theme = useTheme();
  const [exterior, toggleExterior] = useToggle(true);
  const previousExterior = usePrevious(exterior);
  const [is360Loaded, setIs360Loaded] = useState(false);
  const [threeSixtyInitialized, setThreeSixtyInitialized] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [initialProgressPercent, setInitialProgressPercent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isNudeVersion, setIsNudeVersion] = useState(false);
  const threeSixtyInitRef = useRef();

  const changeViewPort = () => {
    document?.getElementById("content").contentWindow?.excite.setViewpoint();
  };

  // This function will check if initExcite function is available on the 360 iframe
  // and it will call it, or retry in 300ms..
  const initThreeSixty = () => {
    const initExcite = document?.getElementById("content")?.contentWindow
      ?.initExcite;
    if (initExcite) {
      initExcite({
        baseAssetUrl: CDN_BASE_URL + "/360/",
        assetVersion: COMMIT_HASH,
      });
      setThreeSixtyInitialized(true);
    } else {
      // Try again in 300ms
      if (threeSixtyInitRef.current) {
        clearTimeout(threeSixtyInitRef.current);
      }
      threeSixtyInitRef.current = setTimeout(initThreeSixty, 300);
    }
  };

  const onExteriorToggle = () => {
    toggleExterior();
    gtm.fire(
      changeModelViewEvent({
        action: exterior ? "interieur" : "exterieur",
        label: t(`carGallery.toggleView.${exterior ? "interior" : "exterior"}`),
      })
    );
  };

  const changePackTo360ByConfiguration = useCallback(
    (id360ViewAccessories, id360ViewPreconfiguration) => {
      if (!is360Loaded || isLoading) {
        if (BUSINESS_ENV !== "production") {
          console.warn(
            `Attempted to change configuration before 360 has finished loading. This could cause an error!`
          );
        }

        return;
      }

      let filteredId360ViewAccessories = [];

      id360ViewAccessories.forEach(accessoryId => {
        if (ALLOWED_ACCESSORY_360_VIEW_IDS.includes(accessoryId)) {
          filteredId360ViewAccessories.push(accessoryId);
        } else if (
          BUSINESS_ENV !== "production" &&
          !ACCESSORIES_WITHOUT_360_MODEL.includes(accessoryId)
        ) {
          console.warn(
            `Accessory with 360 id "${accessoryId}" is not available in the 360 model collection. Please verify if this is a mistake in the 360 id or in the 360 model collection.`
          );
        }
      });

      const excite = document?.getElementById("content").contentWindow?.excite;

      // More info https://drivvn.atlassian.net/browse/APP-19183
      // AMI TONIC has a different ID in the external 360 app, also we need to map some accessories
      if (id360ViewPreconfiguration) {
        // Map accessories
        // Set correct 360 id to tonic
        switch (id360ViewPreconfiguration) {
          case "P011":
            id360ViewPreconfiguration = "P008";
            filteredId360ViewAccessories = [
              "1668279480",
              "1673383780",
              "AC10",
              "AC19",
              "AC14",
              "AC18",
              "AC12",
              "AC01c",
              "1668222080",
              "AC05c",
              "AC06c",
              "1668279880",
              "AC25",
              "AC27",
              "AC41",
              "AC33",
              "AC34",
              "1668281499",
              "1668281480",
              "1674579880",
              "1668280181",
            ];
            break;
        }
      }
      excite?.setColor(id360ViewPreconfiguration);
      excite?.setAccessories(filteredId360ViewAccessories);
    },
    [is360Loaded, isLoading]
  );

  // This allows us to react only to a real change in the configuration, and ignore false signals due to
  // various state changes which may trigger following effect by accident
  const controlHash = `${activePreConfiguration?.id}::${activeAccessory
    .map(({ id }) => id)
    .join(":")}::${is360Loaded ? "loaded" : "loading"}`;
  const previousControlHash = usePrevious(controlHash);

  // Handle configuration / accessory change
  useEffect(() => {
    // If control hash did not change, don't change the 360 accessories
    if (controlHash !== previousControlHash) {
      const {
        id360ViewAccessories,
        id360ViewPreconfiguration,
      } = getId360ViewConfig(activePreConfiguration, activeAccessory, car);

      setIsNudeVersion(id360ViewAccessories.length === 0);

      if (is360Loaded) {
        // We want to change the configuration even if 360 is not open (but is loaded)
        changePackTo360ByConfiguration(
          id360ViewAccessories,
          id360ViewPreconfiguration
        );
      }
    }
  }, [
    activePreConfiguration,
    activeAccessory,
    is360Loaded,
    changePackTo360ByConfiguration,
    controlHash,
    previousControlHash,
  ]);

  // Handle viewport change
  useEffect(() => {
    // react only to exterior change, but only if 360 is loaded and open
    if (
      threeSixtyOpen &&
      is360Loaded &&
      previousExterior !== undefined &&
      previousExterior !== exterior
    ) {
      changeViewPort();
    }
  }, [previousExterior, exterior, is360Loaded, threeSixtyOpen]);

  // If 360 is not loaded, we register custom event listener to listen for when it finishes loading
  useEffect(() => {
    if (is360Loaded) {
      return;
    }

    const handleLoadEvent = () => {
      setIs360Loaded(true);

      if (!onLoad) {
        return;
      }

      onLoad();
    };

    window.document.addEventListener(
      "360HasInitialized",
      handleLoadEvent,
      false
    );

    return () => {
      window.document.removeEventListener(
        "360HasInitialized",
        handleLoadEvent,
        false
      );
    };
  }, [onLoad, is360Loaded]);

  // Handle when loading has been progressed by 360 (initial loading, or loading another preconfiguration)
  useEffect(() => {
    const handleLoadingProgressEvent = e => {
      const { percent } = e.detail;

      if (initialProgressPercent === 0) {
        setInitialProgressPercent(percent);
      }

      if (isNudeVersion) {
        setCurrentLoadingProgress(percent);
      } else {
        if (isFirstLoad) {
          setCurrentLoadingProgress(percent / 2);
        } else {
          // second part of the animation for the second load has weight of 40%
          const remainingPercent = 100 - initialProgressPercent; // 44
          const processedOfRemainingPercent =
            remainingPercent - (100 - percent);
          const ratio = processedOfRemainingPercent / remainingPercent;
          const value = ratio * 40;
          setCurrentLoadingProgress(60 + value);
        }
      }
    };

    window.document.addEventListener(
      "360LoadingProgress",
      handleLoadingProgressEvent,
      false
    );

    return () => {
      window.document.removeEventListener(
        "360LoadingProgress",
        handleLoadingProgressEvent,
        false
      );
    };
  }, [isFirstLoad, initialProgressPercent, isNudeVersion]);

  // Handle when loading has been started by 360 (initial loading, or loading another preconfiguration)
  useEffect(() => {
    const handleLoadingStartedEvent = () => {
      if (isFirstLoad) {
        setCurrentLoadingProgress(0);
        setIsLoading(true);
      }
    };

    window.document.addEventListener(
      "360HasStartedLoading",
      handleLoadingStartedEvent,
      false
    );

    return () => {
      window.document.removeEventListener(
        "360HasStartedLoading",
        handleLoadingStartedEvent,
        false
      );
    };
  }, [isFirstLoad]);

  // Handle when loading is completed by 360 (initial loading, or loading another preconfiguration)
  useEffect(() => {
    const handleCompletedLoadingEvent = () => {
      if (isFirstLoad) {
        setIsFirstLoad(false);
        if (isNudeVersion) {
          setTimeout(() => {
            setShouldDisplayProgressBar(false);
          }, 1000);
        } else {
          setInitialProgressPercent(0);
          // Hack to fill in the timeout void
          setCurrentLoadingProgress(60);
        }
      } else {
        setTimeout(() => {
          setShouldDisplayProgressBar(false);
        }, 1000);
      }
      setIsLoading(false);
    };

    window.document.addEventListener(
      "360HasCompletedLoading",
      handleCompletedLoadingEvent,
      false
    );

    return () => {
      window.document.removeEventListener(
        "360HasCompletedLoading",
        handleCompletedLoadingEvent,
        false
      );
    };
  }, [isFirstLoad, isNudeVersion]);

  // If 360 initialization has not yet occurred, trigger it on open
  useEffect(() => {
    if (!threeSixtyInitialized && threeSixtyOpen) {
      initThreeSixty();
    }
  }, [threeSixtyOpen, threeSixtyInitialized]);

  return (
    <Container>
      <>
        <StyledHeading customColor={primaryColor} align="center">
          {activePreConfiguration.label ?? ""}
        </StyledHeading>
        <ErrorBoundary id="frame">
          <IFrame
            primaryColor={primaryColor}
            // Don't use CDN, so that the Same-origin policy
            // does not prevent js access to the iframe
            src={getStaticAssetURL("/360/o2c.html", { useCdn: false })}
            name="content"
            id="content"
            frameborder="0"
            allowFullScreen
            onmousewheel=""
          />
        </ErrorBoundary>
        {threeSixtyOpen && (
          <>
            {!isLoading && !shouldDisplayProgressBar && (
              <SwitcherWrapper data-id="360-toggle-view-switch-container">
                <StyledTypography
                  bold
                  customColor={
                    !exterior || !is360Loaded ? theme.colors.grey : primaryColor
                  }
                >
                  {t("carGallery.toggleView.exterior")}
                </StyledTypography>
                <Switch
                  data-id="360-toggle-view-button"
                  isHorizontal
                  disabled={!is360Loaded}
                  switchBorderColor={theme.colors.grey}
                  onChange={onExteriorToggle}
                  switchBackgroundColor={theme.colors.transparent}
                  checked={!exterior}
                  primaryColor={!is360Loaded ? theme.colors.grey : primaryColor}
                />
                <StyledTypography
                  bold
                  customColor={!exterior ? primaryColor : theme.colors.grey}
                >
                  {t("carGallery.toggleView.interior")}
                </StyledTypography>
              </SwitcherWrapper>
            )}
          </>
        )}
      </>
    </Container>
  );
};

ThreeSixty.propTypes = {
  threeSixtyOpen: bool.isRequired,
  onLoad: func,
  setCurrentLoadingProgress: func.isRequired,
  shouldDisplayProgressBar: bool.isRequired,
  setShouldDisplayProgressBar: func.isRequired,
};

export default memo(ThreeSixty);
